<template>
  <v-row no-gutters>
    <v-col cols="12">
      <template v-if="isDictAttribute">
        <SelectRow
          v-if="isDictAttribute"
          v-model="computedValue"
          :attribute="{ ...attribute, required: true }"
          :is-label-visible="false"
          :can-edit="true"
          :value-cols="12"
          :input-props="{ label: $i18n.t('dashboard.filters.value') }"
          :row-classes="['no-gutters']"
        />
      </template>
      <template v-else-if="isBooleanAttribute">
        <v-col cols="12" class="pa-0">
          <v-select
            :attach="true"
            v-model="computedValue"
            :items="dicts.boolean"
            :item-text="$i18n.locale"
            item-value="bool"
            outlined
          />
        </v-col>
      </template>
      <v-menu
        v-else
        v-model="isListMenuVisible"
        min-width="300px"
        nudge-bottom="55"
        nudge-left="0"
        :close-on-content-click="false"
        :close-on-click="false"
        :open-on-click="false"
      >
        <template #activator="{ on }">
          <v-text-field
            v-on="on"
            :disabled="!attributeName"
            v-model="computedValue"
            @focus="onFocus"
            @input="resetHints"
            :label="$i18n.t('dashboard.filters.value')"
            hide-details
            outlined
            :class="`included-${idx}`"
            :rules="requiredRules"
          />
        </template>
        <v-list
          v-if="attribute"
          v-click-outside="{
            handler: onClickOutside,
            include,
            closeConditional
          }"
          @scroll.native="scroll($event)"
          class="autocomplete-list"
        >
          <template v-if="items.length > 0">
            <v-list-item v-for="(item, key) of items" :key="key" @click="setValue(item)">{{ item }}</v-list-item>
            <v-list-item v-if="allHintsFetched" class="text-body-2 font-italic">{{
              $i18n.t('default.allHintsFetched')
            }}</v-list-item>
          </template>
          <template v-else>
            <v-list-item class="text-body-2 font-italic">{{ $i18n.t('default.noHints') }}</v-list-item>
          </template>
        </v-list>
      </v-menu>
    </v-col>
  </v-row>
</template>
<script>
import { DICTS } from '@/dicts';
import { debounce } from 'lodash';
import SelectRow from './SelectRow';
export default {
  components: {
    SelectRow
  },
  props: {
    attribute: {
      type: [Object, String]
    },
    entity: {
      type: String,
      required: true
    },
    idx: {
      type: [Number, String],
      required: true
    },
    parentAttributeName: {
      type: String
    },
    publicHints: {
      type: Boolean,
      default: false
    },
    value: {
      default: ''
    }
  },
  computed: {
    dicts: () => DICTS,
    attributeName() {
      return this.attribute.name;
    },
    computedValue: {
      get() {
        return this.value;
      },
      set(nV) {
        this.$emit('update:value', nV);
      }
    },
    isBooleanAttribute() {
      return this.attribute.type === 'boolean';
    },
    isDictAttribute() {
      const dict = ['wojewodztwo', 'powiat', 'gmina'];
      return (
        !dict.includes(this.attribute.name) &&
        (this.attribute.type === 'select' || this.attribute.type === 'multiselect')
      );
    },
    requiredRules() {
      return [v => !!v || v == '0' || this.$i18n.t('default.requiredField')];
    }
  },
  data: () => ({
    allHintsFetched: false,
    isAttributeSupported: true,
    isListMenuVisible: false,
    items: [],
    page: 1
  }),
  methods: {
    closeConditional() {
      return this.isListMenuVisible;
    },
    getFilterHints() {
      if (this.allHintsFetched || !this.isAttributeSupported || !this.attributeName) {
        return;
      }
      const dict = {
        powierzchnia: 'pow_dzialki'
      };
      const attributeName = this.parentAttributeName
        ? `${this.parentAttributeName}.${this.attributeName}`
        : this.attributeName;
      const payload = {
        attribute: dict[attributeName] || attributeName,
        entity: this.entity.substring(0, this.entity.length - 1),
        data: {
          language: this.$i18n.locale,
          filter: this.computedValue
        },
        params: { page: this.page, is_public: this.publicHints }
      };
      if (!payload.attribute) {
        return;
      }
      this.$store
        .dispatch('getFilterHints', payload)
        .then(r => {
          if (r.status === 200) {
            if (r.data.length > 0) {
              this.items = this.items.concat(r.data);
              this.page++;
            } else {
              this.allHintsFetched = true;
            }
          } else {
            if (r.data.error === 'invalid attribute') {
              this.isAttributeSupported = false;
            } else {
              this.$root.$emit('showSnackbar', this.$i18n.t(`default.error`));
            }
          }
        })
        .catch(e => {
          // console.log(e);
        });
    },
    include() {
      return [document.querySelector(`.included-${this.idx}`)];
    },
    onClickOutside() {
      this.isListMenuVisible = false;
    },
    onFocus() {
      if (!this.attribute || !this.isAttributeSupported) {
        return;
      }
      this.isListMenuVisible = true;
    },
    resetHints() {
      this.allHintsFetched = false;
      this.items = [];
      this.isAttributeSupported = true;
      this.page = 1;
      this.getFilterHints();
    },
    scroll(e) {
      if (e.srcElement.scrollTop >= e.srcElement.scrollHeight - e.srcElement.clientHeight) {
        this.getFilterHints();
      }
    },
    setValue(value) {
      this.isListMenuVisible = false;
      this.computedValue = value;
      this.resetHints();
    },
    setEmptyValue() {
      if (this.attribute.type === 'multiselect') {
        this.computedValue = [];
      } else if (this.attribute.type === 'select') {
        this.computedValue = this.attribute.translate ? {} : '';
      } else if (this.attribute.type === 'boolean') {
        this.computedValue = true;
      } else {
        this.computedValue = '';
      }
      this.resetHints();
      this.$emit('resetValidation');
    }
  },
  watch: {
    attribute: {
      deep: true,
      immediate: true,
      handler(nV, oV) {
        if (!oV && Boolean(this.computedValue)) {
          return;
        } 
        this.setEmptyValue();
      }
    }
  },
  created() {
    this.getFilterHints = debounce(this.getFilterHints, 500);
  },
};
</script>
<style lang="scss" scoped>
.autocomplete-list {
  max-height: 300px;
  overflow-y: auto;
}
</style>